// PII sanitizer for every outbound PostHog property bag (GIS-215).
//
// This is the FOURTH copy of a hand-maintained list (gist-pwa, gist-landing,
// gist-firebase-functions, here), and the taxonomy doc names that duplication as
// a real cost rather than a hypothetical one: `cancellationnote` lived only in
// the server copy until GIS-207 caught it. So the structure below is deliberately
// a near-verbatim copy of `gist-pwa/src/analytics/sanitize.js` — same order, same
// comments, same helper names — so that a plain diff between the two shows only
// what is genuinely surface-specific. Everything unique to this surface is
// confined to the clearly marked block at the end of BLOCKED_KEYS and to
// BLOCKED_PATH_PATTERNS, which has no counterpart elsewhere.
//
// The stakes are higher here than on any other surface. This panel renders whole
// user documents as visible text (`src/Users/UserDetailsPopup.js`), so a careless
// `capture("thing", user)` would ship a phone number, an email, a device token
// and a Stripe customer id in one call. The point of this module is that such a
// call CANNOT leak, not that nobody will write it.
//
// It defends on four axes:
//   1. Key blocklist       — `phone`, `email`, `deviceToken`, name fields, ...
//   2. Value scanning      — an email address or an E.164 number in ANY string
//                            value, whatever the key is called.
//   3. URL query scrubbing — identifying query params.
//   4. URL PATH scrubbing  — this surface puts a Firebase uid in the PATH, not
//                            the query string, which axis 3 cannot reach.
//
// Pure and dependency-free so it can be unit-tested without booting PostHog.

const REDACTED = "[redacted]";

// Compared case-insensitively against the exact property key. `name` is here on
// purpose: we never send a display name, and blocking the bare key is what makes
// an accidental `{...user}` spread safe.
const BLOCKED_KEYS = [
  "phone",
  "phonenumber",
  "fullnumber",
  "phonedigits",
  "email",
  "useremail",
  "userphone",
  "devicetoken",
  "fcmtoken",
  "name",
  "fullname",
  "firstname",
  "lastname",
  "displayname",
  "username",
  "reportername",
  "stripecustomerid",
  "clientsecret",
  "client_secret",
  "customprice",
  "photovalidation",
  "password",
  "otp",
  "otpcode",
  "code",
  // Real users-doc fields that a `{...user}` spread would otherwise ship. `bio`
  // and `socialMedia` are member-authored free text and handles — not blocked by
  // any value pattern, so they have to be named.
  "bio",
  "socialmedia",
  "instagram",
  "snapchat",
  "tiktok",
  "linkedin",
  "twitter",
  "handle",
  "address",
  "notes",
  "note",
  "message",
  "messagetext",
  "reason",
  "internalreason",
  "cancellationreason",
  "cancellationnote",
  "highlights",
  "improvements",
  "comments",
  // become-a-host's free-text answers (GIS-207). Not a shape this app renders,
  // but the sanitizers are maintained as copies of one list — a key that exists
  // in only one of them is a leak on the other three.
  "answers",
  "whyhost",
  "experience",
  "sociallink",

  // ---------------------------------------------------------------------
  // ADMIN-PANEL ADDITIONS. Everything above this line is shared with the
  // other three copies; keep it that way so a diff stays readable.
  //
  // These are fields the panel has in scope that the member surfaces never
  // do — it is the only client that reads whole vendor documents, event
  // operational data and the host's Stripe wiring.
  // ---------------------------------------------------------------------
  // `hostname` is the host's display name denormalized onto the event doc, and
  // it is on nearly every event object this app touches.
  "hostname",
  "vendorname",
  // Venue wifi credentials live on the event doc and are handed to attendees at
  // check-in. `password` above catches the nested key; this catches the parent
  // object so the whole pair is dropped rather than half of it.
  "wifi",
  "wifiname",
  "wifipassword",
  // Where the host physically stands, and the code attendees use to find them.
  "hostpin",
  "meetingpoint",
  // Connect wiring. Not credentials, but they are stable identifiers for a real
  // person's bank relationship and have no analytical use.
  "stripeaccountid",
  "stripeconnectid",
  "stripeaccount",
  // The Meetup identity a host links in Settings and profile setup.
  "meetupmemberid",
];

const BLOCKED = new Set(BLOCKED_KEYS);

// Query params stripped out of any URL-valued property.
const BLOCKED_QUERY_PARAMS = ["host", "uid", "userID", "userId", "phone", "email"];

// URL PATH segments that carry an identifier, collapsed to their route pattern.
//
// This axis does not exist in the other three sanitizers because they have no
// route shaped like this. `/hosts/:vendorID/edit` puts a raw Firebase uid in the
// path, and posthog-js auto-attaches `$current_url` to EVERY event — so an
// operator who fires any event while sitting on that screen ships the uid, with
// no capture() call anywhere having mentioned it. Query-param scrubbing cannot
// see a path segment, which is exactly why GIS-215 named this one specifically.
//
// Note this runs even though routes.js never reports that route: the two are
// independent defences, and this one covers events fired from a route the
// allowlist declined to name.
const BLOCKED_PATH_PATTERNS = [
  [/\/hosts\/[^/?#]+\/edit/g, "/hosts/:vendorID/edit"],
];

// Deliberately narrow. Requiring a leading `+` keeps this from eating ordinary
// numeric ids, timestamps or seat counts, while still catching the E.164 values
// this codebase actually passes around.
const PHONE_RE = /\+\d[\d\s().-]{7,}\d/;
const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/;

// Bearer-credential shapes that must never ride along under ANY key. The Stripe
// client_secret is the dangerous one: neither of the patterns above matches it
// (no `+`, no `@`), so without this it would only be caught if someone happened
// to name the key `clientSecret`.
const SECRET_RE = /\b(?:pi|seti|cs)_[A-Za-z0-9]+_secret_[A-Za-z0-9]+/;

// A bare national-format phone (no leading +). Deliberately anchored to a
// 3-3-4 shape with a separator so it cannot eat ids, prices or timestamps —
// "415-555-0123" and "(415) 555 0123" match; "1234567890" does not.
//
// The optional `\)` is not decoration. Without it the parenthesised form — what
// a browser autofill or a paste from Contacts actually produces — sails through,
// which a test caught after all three copies of this file had carried a comment
// claiming it was covered.
const LOOSE_PHONE_RE = /\b\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}\b/;

// Guards against a pathological or cyclic object costing us the main thread.
//
// 6, not 4, because of exception autocapture. posthog nests a captured error as
// `$exception_list[] -> stacktrace -> frames[] -> frame`, which puts a frame
// object at depth 4 exactly — so a cap of 4 replaced EVERY stack frame with the
// string "[redacted]" and left error tracking with a type and a message and no
// stack at all. Raising the cap costs nothing in safety: the depth limit is a
// cost guard, not a PII control, and walking further means MORE strings get
// scanned, not fewer. Arrays are still capped independently.
const MAX_DEPTH = 6;
const MAX_ARRAY_ITEMS = 50;

function looksLikeUrl(value) {
  return /^https?:\/\//i.test(value) || value.startsWith("/");
}

/**
 * Replace identifier-bearing path segments with their route pattern. Applied to
 * absolute URLs and bare paths alike, before any parsing, so a malformed URL
 * that `scrubUrl` gives up on is still covered.
 */
export function scrubPath(value) {
  if (typeof value !== "string" || !value) return value;
  return BLOCKED_PATH_PATTERNS.reduce(
    (acc, [pattern, replacement]) => acc.replace(pattern, replacement),
    value
  );
}

/**
 * Strip identifying query params out of a URL string. Handles both absolute
 * URLs and bare paths, and leaves anything unparseable alone rather than
 * throwing inside a capture call.
 */
export function scrubUrl(value) {
  if (typeof value !== "string" || !value) return value;
  // Path scrubbing runs FIRST and unconditionally. `new URL()` throws on plenty
  // of real inputs, and the early return below skips the rest when no query
  // param matched — either path would otherwise leave the uid in place.
  const pathScrubbed = scrubPath(value);
  try {
    // A base is required for relative paths; it is discarded below.
    const isAbsolute = /^https?:\/\//i.test(pathScrubbed);
    const url = new URL(pathScrubbed, isAbsolute ? undefined : "https://placeholder.invalid");
    let touched = false;
    BLOCKED_QUERY_PARAMS.forEach((param) => {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param);
        touched = true;
      }
    });
    if (!touched) return pathScrubbed;
    return isAbsolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
  } catch (err) {
    return pathScrubbed;
  }
}

function isSensitiveString(value) {
  return (
    PHONE_RE.test(value) ||
    EMAIL_RE.test(value) ||
    SECRET_RE.test(value) ||
    LOOSE_PHONE_RE.test(value)
  );
}

function sanitizeString(value) {
  if (looksLikeUrl(value)) {
    const scrubbed = scrubUrl(value);
    // A URL can still embed a phone, email or secret in a path segment.
    return isSensitiveString(scrubbed) ? REDACTED : scrubbed;
  }
  return isSensitiveString(value) ? REDACTED : value;
}

function sanitizeValue(value, depth) {
  if (typeof value === "string") return sanitizeString(value);
  if (value === null || typeof value !== "object") return value;
  if (depth >= MAX_DEPTH) return REDACTED;

  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_ARRAY_ITEMS)
      .map((item) => sanitizeValue(item, depth + 1));
  }
  return sanitizeObject(value, depth + 1);
}

function sanitizeObject(input, depth) {
  const output = {};
  Object.keys(input).forEach((key) => {
    if (BLOCKED.has(key.toLowerCase())) {
      // Dropped outright rather than redacted — a `[redacted]` phone key in the
      // PostHog property list still tells you we tried to send one.
      return;
    }
    // Keys are scrubbed too, and only a URL-shaped one can carry anything. This
    // exists for `$heatmap_data`, which posthog keys BY the page url: every rule
    // in this file reads values, so without this the uid-bearing paths that
    // BLOCKED_PATH_PATTERNS strips out of $current_url would come straight back
    // as an object key.
    const safeKey = looksLikeUrl(key) ? scrubUrl(key) : key;
    output[safeKey] = sanitizeValue(input[key], depth);
  });
  return output;
}

/**
 * posthog-js `sanitize_properties` hook. Runs on EVERY event, including
 * library-internal ones ($pageview, $identify).
 *
 * Also nulls `$ip`. Be clear about what that does and does not achieve.
 * VERIFIED 2026-07-30: it is inert. PostHog overwrites `$ip` from the REQUEST ip
 * during server-side ingestion, then GeoIP-enriches it onto the person profile.
 * The only control is the PROJECT setting `anonymize_ips`; there is no
 * client-side equivalent, so nothing in this file can change it either way.
 *
 * GeoIP is ON deliberately (taxonomy §9, decision 5, 2026-07-30). Do not remove
 * the line below thinking it turns anything off, and do not add anything next to
 * it thinking that would either. It is a marker that the question was asked and
 * settled elsewhere.
 *
 * @param {Object} properties raw property bag from posthog-js
 * @returns {Object} a new, safe property bag
 */
export function sanitizeProperties(properties) {
  if (!properties || typeof properties !== "object") return properties;
  const safe = sanitizeObject(properties, 0);
  safe.$ip = null;
  return safe;
}

export const _internal = {
  BLOCKED_KEYS,
  BLOCKED_QUERY_PARAMS,
  BLOCKED_PATH_PATTERNS,
  REDACTED,
};
